import type { NamedSet } from 'zustand/middleware';

import { months } from '../../constants/appConstants';
import type { SettlementType } from '../../repositories/settlementRepository';
import type { SortBy } from '../../utils/dateUtils';
import { compareSettlementDates } from '../../utils/dateUtils';
import type { ZustandStore } from '../store.type';

type SettlementCardInfo = {
	createdAt: Date;
	teamId: string;
	userInfo: {
		userId: string;
		name: string;
	};
	requestTitle: string;
	requestAmount: string;
	cardType: string;
	id: string;
	billingAddress: {
		addressLine1: string;
		city: string;
		countryCode: string;
		region: string;
		zipCode: string;
	};
	value: number;
	balance: number;
	currency: string;
	expiryDate: string;
	expiry: string;
	validEndingOn: string;
	lastDigits: string;
	owner: string;
	status: string;
	provider: string;
	provisioner: string;
	sourceId: string;
};

type CardSourceData = {
	billingAddress: {
		addressLine1: string;
		city: string;
		countryCode: string;
		region: string;
		zipCode: string;
	};
	id: string;
	name: string;
	provider: string;
	provisioners: string[];
	state: string;
};

type Costcentercode = {
	ID: string;
	path: string;
	listId: string;
	listLabel: string;
};

export type Coding = {
	costcentercode: Costcentercode;
};

type FilterInfo = {
	parentCard: string;
	virtualCard: string;
	month: string;
	year: string;
	showTransactionWithoutReceipts: boolean;
};

export type SettlementCardType = {
	label: string;
	id: string;
};

type MonthType = {
	name: string;
	value: number;
};

type FilterOptions = {
	parentCards: SettlementCardType[];
	virtualCards: SettlementCardType[];
	months: MonthType[];
	years: number[];
};

export type Settlement = {
	amount: number;
	cardId: string;
	cardInfo: SettlementCardInfo;
	cardSourceId: string;
	currency: string;
	date?: Date;
	id: string;
	merchant: {
		categoryCode: string;
		id: string;
		name: string;
	};
	provider: string;
	totalAmount: string;
	transactionId: string;
	transactionReferenceNumber: string;
	receiptId: string | undefined;
	cardSource: CardSourceData;
	codingInfo: Coding;
	type?: SettlementType;
};

type SettlementState = {
	settlement: {
		settlements: Settlement[];
		filters: FilterInfo;
		options: FilterOptions;
		page: number;
		areSettlementFiltersManuallySet: boolean;
		isReceiptUploadRemove: boolean;
	};
};
const date = new Date();
const currentYear = date.getFullYear();

const settlementInitialState: SettlementState = {
	settlement: {
		settlements: [],
		filters: {
			parentCard: 'allSourceCards',
			virtualCard: 'allVirtualCards',
			month: String(months?.[date.getMonth()]?.value),
			year: String(currentYear),
			showTransactionWithoutReceipts: false,
		},
		areSettlementFiltersManuallySet: false,
		options: {
			parentCards: [],
			virtualCards: [],
			months: [],
			years: [],
		},
		page: 1,
		isReceiptUploadRemove: false,
	},
};

type SettlementActions = {
	setSettlements: (settlements: SettlementState['settlement']['settlements'], sortBy?: SortBy) => void;
	setSettlementFilters: (filters: SettlementState['settlement']['filters']) => void;
	setSettlementOptions: (options: SettlementState['settlement']['options']) => void;
	setPage: (page: SettlementState['settlement']['page']) => void;
	resetSettlementFilters: () => void;
	setAreSettlementFiltersManuallySet: (
		areSettlementFiltersManuallySet: SettlementState['settlement']['areSettlementFiltersManuallySet'],
	) => void;
	setIsReceiptUploadRemove: (isReceiptUploadRemove: SettlementState['settlement']['isReceiptUploadRemove']) => void;
};

export type SettlementSlice = SettlementState & SettlementActions;

export function settlementSlice(set: NamedSet<ZustandStore>): SettlementSlice {
	return {
		...settlementInitialState,
		setSettlements(settlements: SettlementState['settlement']['settlements'], sortBy: SortBy = 'most-recent'): void {
			set(
				(state: SettlementState) => {
					const sortedSettlements = [...settlements]?.sort((a, b) => compareSettlementDates(a, b, sortBy));
					state.settlement.settlements = sortedSettlements;
					return state;
				},
				false,
				'[setSettlements]',
			);
		},
		resetSettlementFilters(): void {
			set(
				(state: SettlementState) => {
					state.settlement.filters.parentCard = settlementInitialState.settlement.filters.parentCard;
					state.settlement.filters.virtualCard = settlementInitialState.settlement.filters.virtualCard;
					state.settlement.filters.month = settlementInitialState.settlement.filters.month;
					state.settlement.filters.year = settlementInitialState.settlement.filters.year;
					state.settlement.filters.showTransactionWithoutReceipts =
						settlementInitialState.settlement.filters.showTransactionWithoutReceipts;
					return state;
				},
				false,
				'[resetSettlementFilters]',
			);
		},
		setSettlementFilters(filters: SettlementState['settlement']['filters']): void {
			set(
				(state: SettlementState) => {
					state.settlement.filters = filters;
					return state;
				},
				false,
				'[setSettlementFilters]',
			);
		},
		setSettlementOptions(options: SettlementState['settlement']['options']): void {
			set(
				(state: SettlementState) => {
					state.settlement.options = options;
					return state;
				},
				false,
				'[setSettlementOptions]',
			);
		},
		setPage(page: SettlementState['settlement']['page']): void {
			set(
				(state: SettlementState) => {
					state.settlement.page = page;
					return state;
				},
				false,
				'[setPage]',
			);
		},
		setAreSettlementFiltersManuallySet(
			areSettlementFiltersManuallySet: SettlementState['settlement']['areSettlementFiltersManuallySet'],
		): void {
			set(
				(state: SettlementState) => {
					state.settlement.areSettlementFiltersManuallySet = areSettlementFiltersManuallySet;
					return state;
				},
				false,
				'[setAreSettlementFiltersManuallySet]',
			);
		},
		setIsReceiptUploadRemove(isReceiptUploadRemove: SettlementState['settlement']['isReceiptUploadRemove']): void {
			set(
				(state: SettlementState) => {
					state.settlement.isReceiptUploadRemove = isReceiptUploadRemove;
					return state;
				},
				false,
				'[setIsReceiptUploadRemove]',
			);
		},
	};
}
